import type { KeyboardEvent } from 'react';
import { Input, Tag, Button } from 'antd';
import { SearchOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { EffectiveChoiceItem } from '../../types/survey.types';
import { getDistinctFilterValues } from '../../utils/questionOptions';
import './QuestionFilterBar.css';

interface QuestionFilterBarProps {
  choices: EffectiveChoiceItem[];
  allowsTypedFilter: boolean;
  selectedFilters: string[];
  filterText: string;
  onSelectedFiltersChange: (filters: string[]) => void;
  onFilterTextChange: (text: string) => void;
  hasVisibleResults: boolean;
  /**
   * Spec 033 (adendo): quando definido, as opções ainda não foram reveladas porque a
   * pergunta está configurada para exigir um filtro primeiro (útil em listas com
   * centenas de opções, ex.: candidatos a deputado estadual/federal). O número indica
   * quantas opções existem ao todo, para o respondente saber o que esperar.
   */
  hiddenOptionsCount?: number;
  /**
   * Spec 033 (adendo): rótulo curto e opcional exibido acima dos controles de filtro
   * (ex.: "Filtre os candidatos pelo partido:"), configurado por pergunta.
   */
  filterLabel?: string | null;
}

/**
 * Spec 033: barra de filtro exibida entre o enunciado e as opções de uma pergunta de
 * múltipla escolha/caixas de seleção — botões de filtro (multi-seleção, lógica "ou")
 * combinados com um campo de texto digitado (lógica "e"), mais um botão "Limpar".
 * Em telas largas, os controles ficam em uma única linha; em telas estreitas, quebram
 * naturalmente por seção (botões, depois busca, depois limpar).
 */
export const QuestionFilterBar = ({
  choices,
  allowsTypedFilter,
  selectedFilters,
  filterText,
  onSelectedFiltersChange,
  onFilterTextChange,
  hasVisibleResults,
  hiddenOptionsCount,
  filterLabel,
}: QuestionFilterBarProps) => {
  const distinctFilterValues = getDistinctFilterValues(choices);

  if (distinctFilterValues.length === 0 && !allowsTypedFilter) {
    return null;
  }

  const toggleFilter = (filterValue: string, checked: boolean) => {
    onSelectedFiltersChange(
      checked ? [...selectedFilters, filterValue] : selectedFilters.filter((f) => f !== filterValue)
    );
  };

  const handleClear = () => {
    onSelectedFiltersChange([]);
    onFilterTextChange('');
  };

  return (
    <div className="question-filter-bar" data-testid="question-filter-bar">
      {filterLabel && <div className="question-filter-bar__label">{filterLabel}</div>}
      <div className="question-filter-bar__row">
        {distinctFilterValues.length > 0 && (
          <div className="question-filter-bar__tags">
            {distinctFilterValues.map((filterValue) => {
              const isChecked = selectedFilters.includes(filterValue);
              // CheckableTag renders a plain <span onClick> with no keyboard support and
              // its TS types don't declare span attributes, even though the component
              // spreads restProps onto the span at runtime — so these a11y attributes
              // (verified against antd's source) need the narrow `any` below to pass
              // through the closed CheckableTagProps type.
              const keyboardA11yProps: any = {
                tabIndex: 0,
                role: 'checkbox',
                'aria-checked': isChecked,
                onKeyDown: (e: KeyboardEvent) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    toggleFilter(filterValue, !isChecked);
                  }
                },
              };
              return (
                <Tag.CheckableTag
                  key={filterValue}
                  checked={isChecked}
                  onChange={(checked) => toggleFilter(filterValue, checked)}
                  {...keyboardA11yProps}
                >
                  {filterValue}
                </Tag.CheckableTag>
              );
            })}
          </div>
        )}

        {allowsTypedFilter && (
          <Input
            data-testid="question-filter-text-input"
            className="question-filter-bar__search"
            allowClear
            prefix={<SearchOutlined />}
            placeholder="Buscar por rótulo/descrição..."
            value={filterText}
            onChange={(e) => onFilterTextChange(e.target.value)}
          />
        )}

        <Button
          data-testid="question-filter-clear-button"
          className="question-filter-bar__clear"
          size="small"
          onClick={handleClear}
        >
          Limpar
        </Button>
      </div>

      {hiddenOptionsCount !== undefined ? (
        <div className="question-filter-bar__empty">
          <InfoCircleOutlined />
          <span>
            {hiddenOptionsCount === 1 ? '1 opção disponível' : `${hiddenOptionsCount} opções disponíveis`} —{' '}
            <span className="question-filter-bar__hint">use os filtros acima para ver a lista.</span>
          </span>
        </div>
      ) : (
        !hasVisibleResults && (
          <div className="question-filter-bar__empty">
            <InfoCircleOutlined />
            <span>Nenhuma opção corresponde ao filtro aplicado</span>
          </div>
        )
      )}
    </div>
  );
};

export default QuestionFilterBar;
